import { ISidenav, Icons } from '@tecalisdev/infinity';

/** Banner promocional expuesto en `/user/info` (BannerResource). */
export interface IUserBanner {
	banner_id: string;
	title?: string;
	image_path?: string;
	image_url?: string | null;
	start_at?: string;
	end_at?: string | null;
	/** Vacío / ausente = alcance global. */
	structure_element_ids?: string[] | null;
	original_name?: string;
	created_at?: string;
	updated_at?: string;
}

export interface IUserResponseInfo {
	user?: IUserResponseInfoUser;
	permissions?: Record<string, TPermissionValue>;
	banners?: IUserBanner[];
	theme?: IResolvedPanelTheme;
	menu?: {
		back: IUserResponseInfoMenu[];
		front: IUserResponseInfoMenu[];
	};
}

export interface IResolvedPanelTheme {
	panel_theme_id?: string | null;
	name?: string | null;
	/** Envelope `{mode, brand, tokens}` o mapa plano legacy `--*`. */
	tokens?: Record<string, unknown> | Record<string, string>;
	/** Logo del topnav (URL temporal firmada). */
	logo_url?: string | null;
	source?: 'global' | 'structure_element' | 'default';
	structure_element_id?: string | null;
}
export interface IUserResponseInfoUser {
	user_id?: string;
	name?: string;
	username?: string;
	last_access_at?: string;
	role?: string;
	structure_element_id?: string;
	structure_id?: string | null;
	sfid?: string | null;
	is_impersonated?: boolean;
}

export interface IUserResponseInfoMenu {
	url: string;
	text: string;
	icon: Icons;
	alert: boolean;
	status: string;
	title?: string;
	divider?: boolean;
	children: IUserResponseInfoMenu[];
	/** Id del enlace externo en `provider_menu_link` (solo ítems external). */
	id?: number;
	external?: boolean;
}

export interface IUserInfo {
	user?: IUser;
	permissions?: Record<string, TPermissionValue>;
	banners?: IUserBanner[];
	theme?: IResolvedPanelTheme;
	menu?: {
		back?: ISidenav[];
		front?: ISidenav[];
	};
}

export interface IUser {
	user_id?: string;
	name?: string;
	username?: string;
	last_access_at?: string;
	role?: string;
	structure_element_logo?: string;
	structure_element_id?: string;
	structure_id?: string | null;
	sfid?: string | null;
	sell_mobile?: boolean;
	is_impersonated?: boolean;
	email?: string; // TODO: fase 2 - no disponible en /user/info
	avatar?: string; // TODO: fase 2 - no disponible en /user/info
}


export type TPermissionValue = PermissionType;

export enum PermissionType {
	ALLOW = "permitir",
	DENY = "no_permitir"
}

export enum Permission {
	BACK_ACCESS = 'back_access',
	FRONT_ACCESS = 'front_access',
	FRONT_MENU_COMMISSIONS = 'front_menu_commissions',
	FRONT_MENU_COMMUNICATIONS = 'front_menu_communications',
	BACK_MENU_BUSINESS_MANAGER = 'back_menu_business_manager',
	BACK_MENU_BUSINESS_MANAGER_FOLLOW_UP = 'back_menu_business_manager_follow_up',
	BACK_MENU_BUSINESS_MANAGER_SIMULATOR = 'back_menu_business_manager_simulator',
	/** Editar PVP personalizado en el simulador comercial (CHF-225). */
	BACK_EDIT_SIMULATOR_CUSTOM_PVP = 'back_edit_simulator_custom_pvp',
	BACK_MENU_OPERATIONS_ACTIVATIONS = 'back_menu_orders',
	BACK_MENU_OPERATIONS_CANCELLATIONS = 'back_menu_operations_cancellations',
	BACK_MENU_OPERATIONS_AFTER_SALES = 'back_menu_operations_after_sales',
	BACK_MENU_OPERATIONS_CUSTOMER = 'back_menu_operations_customer',
	BACK_MENU_OPERATIONS_PORTABILITIES_PENDING = 'back_menu_operations_portabilities_pending',
	BACK_MENU_OPERATIONS_TICKETS = 'back_menu_operations_tickets',
	BACK_UPDATE_TICKET = 'back_update_ticket',
	BACK_DELETE_TICKET = 'back_delete_ticket',
	BACK_MENU_OPERATIONS_SCORING = 'back_menu_operations_scoring',
	BACK_MENU_ADMINISTRATION_STRUCTURES = 'back_menu_administration_structures',
	BACK_MENU_ADMINISTRATION_STRUCTURE_ELEMENTS = 'back_menu_administration_structure_elements',
	BACK_GET_STRUCTURE_ELEMENT = 'back_get_structure_element',
	BACK_CREATE_STRUCTURE_ELEMENT = 'back_create_structure_element',
	BACK_UPDATE_STRUCTURE_ELEMENT = 'back_update_structure_element',
	BACK_DELETE_STRUCTURE_ELEMENT = 'back_delete_structure_element',
	/** Listado canales de venta (pestaña Ventas en detalle SE root). */
	BACK_LIST_SALES_CHANNELS = 'back_list_sales_channels',
	BACK_MENU_ADMINISTRATION_USERS = 'back_menu_administration_users',
	BACK_MENU_ADMINISTRATION_BRANDING = 'back_menu_administration_branding',
	BACK_MENU_ADMINISTRATION_MENU_LINKS = 'back_menu_administration_menu_links',
	BACK_CREATE_MENU_LINKS = 'back_create_menu_links',
	BACK_GET_MENU_LINKS = 'back_get_menu_links',
	BACK_UPDATE_MENU_LINKS = 'back_update_menu_links',
	BACK_DELETE_MENU_LINKS = 'back_delete_menu_links',
	BACK_EDIT_USER_PERMISSION = 'back_edit_user_permission',
	BACK_MENU_ADMINISTRATION_ROLES = 'back_menu_administration_roles',
	BACK_MENU_ADMINISTRATION_LOCKS = 'back_menu_administration_locks',
	BACK_MENU_STRUCTURE_ADMINS = 'back_menu_structure_admins',
	BACK_TOGGLE_STRUCTURE_ELEMENT_STATUS = 'back_toggle_structure_element_status',
	BACK_LIST_STRUCTURE_ELEMENT_RISK_RANKING = 'back_list_structure_element_risk_ranking',
	BACK_GET_STRUCTURE_ELEMENT_PERFORMANCE = 'back_get_structure_element_performance',
	BACK_GET_STRUCTURE_ELEMENT_SALE_QUALITY = 'back_get_structure_element_sale_quality',
	BACK_LIST_PENDING_STRUCTURE_ELEMENTS = 'back_list_pending_structure_elements',
	BACK_UPDATE_STRUCTURE_ELEMENT_PLANNED_VISIT = 'back_update_structure_element_planned_visit',
	/** Panel FO: editar conectividad de la propia tienda (CHF-798/799). */
	FRONT_UPDATE_STRUCTURE_ELEMENT_CONNECTIVITY = 'front_update_structure_element_connectivity',
	BACK_CREATE_STRUCTURE_ELEMENT_VISIT = 'back_create_structure_element_visit',
	BACK_MENU_CATALOG = 'back_menu_catalog',
	BACK_MENU_BILLING_DASHBOARD = 'back_menu_billing_dashboard',
	BACK_MENU_BILLING_RECORDS = 'back_menu_billing_records',
	BACK_MENU_BILLING_PROFORMAS = 'back_menu_billing_proformas',
	BACK_MENU_BILLING_INVOICES = 'back_menu_billing_invoices',
	BACK_MENU_BILLING_OVERCOSTS = 'back_menu_billing_overcosts',
	BACK_MENU_BILLING_NOTE = 'back_menu_billing_note',
	BACK_MENU_BILLING_REPORTS = 'back_menu_billing_reports',
	BACK_MENU_BILLING_CONFIG = 'back_menu_billing_config',
	BACK_GET_BILLING_OVERCOSTS = 'back_get_billing_overcosts',
	BACK_GET_BILLING_RECORD = 'back_get_billing_record',
	BACK_CREATE_BILLING_PROFORMA = 'back_create_billing_proforma',
	BACK_CREATE_BILLING_INVOICE = 'back_create_billing_invoice',
	BACK_GET_BILLING_PROFORMA = 'back_get_billing_proforma',
	BACK_SEND_BILLING_PROFORMA = 'back_send_billing_proforma',
	BACK_UPDATE_BILLING_PROFORMA = 'back_update_billing_proforma',
	BACK_GET_BILLING_INVOICE = 'back_get_billing_invoice',
	BACK_UPDATE_BILLING_INVOICE = 'back_update_billing_invoice',
	BACK_CREATE_BILLING_INVOICE_RECTIFICATION = 'back_create_billing_invoice_rectification',
	BACK_DELETE_BILLING_INVOICE = 'back_delete_billing_invoice',
	BACK_MENU_PAYDEX = 'back_menu_paydex',
	BACK_MENU_PAYDEX_LIQUIDATIONS = 'back_menu_paydex_liquidations',
	BACK_MENU_PAYDEX_BILLING = 'back_menu_paydex_billing',
	BACK_MENU_PAYDEX_INCENTIVE = 'back_menu_paydex_incentive',
	BACK_MENU_COMMUNICATIONS = 'back_menu_communications',
	BACK_MENU_COMMUNICATIONS_ALERTS = 'back_menu_communications_alerts',
	BACK_MENU_COMMUNICATIONS_ANNOUNCEMENT = 'back_menu_communications_announcement',
	BACK_MENU_COMMUNICATIONS_BANNER = 'back_menu_communications_banner',
	BACK_GET_RESOURCE = 'back_get_resource',
	BACK_CREATE_RESOURCE = 'back_create_resource',
	BACK_UPDATE_RESOURCE = 'back_update_resource',
	BACK_DELETE_RESOURCE = 'back_delete_resource',
	/** Menú admin O2 Academy (subir recursos). Solo admin. CHF-782. */
	BACK_MENU_RESOURCES = 'back_menu_resources',
	BACK_MENU_ADMINISTRATION_TARIFFS = 'back_menu_administration_tariffs',
	BACK_GET_TARIFF = 'back_get_tariff',
	BACK_CREATE_TARIFF = 'back_create_tariff',
	BACK_UPDATE_TARIFF = 'back_update_tariff',
	BACK_DELETE_TARIFF = 'back_delete_tariff',
	BACK_MENU_REPORTS = 'back_menu_reports',
	BACK_MENU_IMPORT = 'back_menu_import',
	BACK_MENU_ACCOUNT = 'back_menu_account',
}

export enum UserRole {
	ADMIN = 'admin',
	AGENT = 'agent',
	DISTRIBUTOR = 'distributor',
	GPV = 'gpv',
	TECHNICAL_SALES = 'technical_sales',
	/** Jefe de equipo comercial (CHF-706 / CHF-707). */
	TEAM_LEAD = 'team_lead',
	POS = 'pos',
	SELL_GENERAL = 'sell_general',
}

export enum BackofficeRoute {
	ROOT = '/admin',
	// Panel
	PANEL = '/admin',
	// Gestión comercial
	LEADS = '/admin/business-manager/leads',
	FOLLOW_UPS = '/admin/business-manager/follow-ups',
	OPPORTUNITIES = '/admin/business-manager/opportunities',
	PIPELINE = '/admin/business-manager/pipeline',
	SIMULATOR = '/admin/business-manager/simulator',
	AGENTS = '/admin/business-manager/agents',
	// Operaciones
	OPERATIONS = '/admin/operations',
	ORDERS = '/admin/operations/activations',
	ORDER_DETAIL = '/admin/operations/detail',
	AFTER_SALES = '/admin/operations/after-sales',
	POSTSELL_DETAIL = '/admin/operations/after-sales/detail',
	CANCELLATIONS = '/admin/operations/cancellations',
	CUSTOMERS = '/admin/customers',
	INCIDENTS = '/admin/operations/tickets',
	ORDER_SCORING = '/admin/operations/scoring',
	// Administración
	STRUCTURES = '/admin/administration/structures',
	STRUCTURE_ELEMENTS = '/admin/administration/structure-elements',
	STRUCTURE_ELEMENT_DETAIL = '/admin/administration/structure-elements/detail',
	STRUCTURE_LEVELS = '/admin/administration/structure-levels',
	ROLES = '/admin/administration/roles',
	USERS = '/admin/administration/users',
	BRANDING = '/admin/administration/branding',
	MENU_LINKS = '/admin/administration/menu-links',
	LOCKS = '/admin/administration/locks',
	// Catalogo
	TARIFFS = '/admin/catalog/tariffs',
	// Facturacion
	BILLING_CONFIGURATION = '/admin/billing/config',
	BILLING_PAYMENT_TYPES = '/admin/billing/config/payment-types',
	BILLING_BANK_ACCOUNTS = '/admin/billing/config/bank-accounts',
	BILLING_COMPANIES = '/admin/billing/config/billing-companies',
	BILLING_COMPANY_DETAIL = '/admin/billing/config/billing-companies',
	BILLING_COMPANY_CREATE = '/admin/billing/config/billing-companies/create',
	BILLING_COMPANY_EDIT = '/admin/billing/config/billing-companies/edit',
	BILLING_INVOICES = '/admin/billing/invoices',
	BILLING_INVOICE_DETAIL = '/admin/billing/invoices/detail',
	BILLING_DASHBOARD = '/admin/billing/dashboard',
	BILLING_RECORDS = '/admin/billing/records',
	BILLING_RECORD_DETAIL = '/admin/billing/records/detail',
	BILLING_PROFORMAS = '/admin/billing/proformas',
	BILLING_PROFORMA_DETAIL = '/admin/billing/proformas/detail',
	BILLING_OVERCOSTS = '/admin/billing/overcosts',
	BILLING_OVERCOST_DETAIL = '/admin/billing/overcosts/detail',
	// Comunicaciones
	COMMUNICATIONS = '/admin/communications',
	ANNOUNCEMENTS = '/admin/communications/announcements',
	NEWSLETTERS = '/admin/communications/newsletters',
	BANNERS = '/admin/communications/banners',
	ALERTS = '/admin/communications/alerts',
	RESOURCES = '/admin/resources',
	// Informes
	REPORTS = '/admin/reports',
	// Importar archivos
	IMPORT = '/admin/import',
	IMPORT_HISTORY = '/admin/import/history',
	// Paydex
	PAYDEX_LIQUIDATIONS = '/admin/paydex/liquidations',
	PAYDEX_LIQUIDATION_DETAIL = '/admin/paydex/liquidations/detail',
	PAYDEX_BILLING = '/admin/paydex/billing',
	PAYDEX_BILLING_DETAIL = '/admin/paydex/billing/detail',
	PAYDEX_INCENTIVE = '/admin/paydex/incentive',
	PAYDEX_INCENTIVE_CREATE = '/admin/paydex/incentive/create',
	PAYDEX_INCENTIVE_EDIT = '/admin/paydex/incentive/edit',
}

export enum FrontofficeRoute {
	ROOT = '/ventas',
	OPERATIONS = '/ventas/operations',
	SERVICES = '/ventas/services',
	SERVICES_FTTH = '/ventas/services/ftth',
	SERVICES_BUNDLE = '/ventas/services/bundle',
	SERVICES_MOBILE = '/ventas/services/mobile',
	TARIFFS = '/ventas/tarifas',
	COMMISSIONS = '/ventas/commissions',
	RESOURCES = '/ventas/resources',
	COMMUNICATIONS = '/ventas/communications',
	CALLBACK_OK = '/ventas/ok',
	CALLBACK_CANCEL = '/ventas/cancel',
	CALLBACK_KO = '/ventas/ko',
}
